import React, { useState, useRef, useEffect, useMemo } from 'react';

// "+ Add or create" multi-select for linking a client to Offerings (Veronica's
// design doc p6). Same search/scroll/pinned-footer shape as ClientAddPicker.js,
// kept standalone rather than shared: stays open after a row is clicked so a
// coach can link several offerings in one pass; closes on click-outside/Escape.
// Only active offerings are listed, but an already-linked inactive one still
// shows so it can be unlinked. The parent renders the selected chips.
export default function OfferingAddPicker({ offerings, selectedOfferingIds, onToggle, onCreateNew }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return offerings
      .filter(o => o.is_active !== false || selectedOfferingIds.includes(o.id))
      .filter(o => !q || o.name.toLowerCase().includes(q));
  }, [offerings, selectedOfferingIds, search]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch('');
      searchInputRef.current?.focus();
    }
  }, [open]);

  return (
    <div style={styles.wrapper} ref={wrapperRef}>
      <button type="button" style={styles.addBtn} onClick={() => setOpen(o => !o)}>
        + Add or create
      </button>

      {open && (
        <div style={styles.popover}>
          <input
            ref={searchInputRef}
            style={styles.searchInput}
            placeholder="Search offerings"
            value={search}
            onChange={e => setSearch(e.target.value)}
            // Lives inside the New client <form> — Enter here must not submit it.
            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
          />
          <div style={styles.list}>
            {filtered.map(o => {
              const selected = selectedOfferingIds.includes(o.id);
              const isGroup = o.format === 'group';
              return (
                <div key={o.id} style={styles.row} onClick={() => onToggle(o.id)}>
                  <span style={styles.rowName}>{o.name}</span>
                  {o.format && (
                    <span style={{ ...styles.badge, background: isGroup ? 'var(--border-light)' : 'var(--gold-pale)', color: isGroup ? 'var(--text-mid)' : 'var(--gold)' }}>
                      {isGroup ? 'Group' : '1:1'}
                    </span>
                  )}
                  <span style={{ ...styles.check, visibility: selected ? 'visible' : 'hidden' }}>✓</span>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={styles.emptyState}>{search ? `No offerings match "${search}".` : 'No offerings yet.'}</div>
            )}
          </div>
          <div style={styles.footer} onClick={() => { onCreateNew(); setOpen(false); }}>
            <span style={styles.footerAdd}>+ New offering type</span>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: { position: 'relative', display: 'inline-block' },
  addBtn: { padding: '6px 12px', borderRadius: '20px', border: '1px dashed var(--gold)', background: 'white', color: 'var(--gold)', fontFamily: "'DM Sans', sans-serif", fontSize: '12.5px', fontWeight: '500', cursor: 'pointer' },
  popover: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, width: '300px', zIndex: 50, background: 'white', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', maxHeight: '320px', overflow: 'hidden' },
  searchInput: { width: '100%', padding: '12px 14px', border: 'none', borderBottom: '1px solid var(--border-light)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box', flexShrink: 0 },
  list: { overflowY: 'auto', flex: 1 },
  row: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)' },
  rowName: { fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', flex: 1 },
  badge: { fontSize: '11px', padding: '2px 8px', borderRadius: '20px', fontWeight: '500', whiteSpace: 'nowrap' },
  check: { color: 'var(--sage)', fontSize: '13px', fontWeight: '700', width: '12px' },
  emptyState: { padding: '16px 14px', fontSize: '13px', color: 'var(--text-soft)' },
  footer: { padding: '11px 14px', borderTop: '1px solid var(--border-light)', background: 'var(--mint)', cursor: 'pointer', flexShrink: 0 },
  footerAdd: { fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', fontWeight: '600', color: 'var(--sage)' },
};
